import React from 'react'
import { Card, CardContent, CardHeader, CardTitle } from './ui/card'
import { Button } from './ui/button'
import { Input } from './ui/input'
import { Textarea } from './ui/textarea'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select'
import { Checkbox } from './ui/checkbox'
import { Badge } from './ui/badge'
import { Label } from './ui/label'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog'
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from './ui/collapsible'
import { CheckCircleIcon, ChevronRightIcon, ChevronDownIcon } from '@heroicons/react/24/outline'
import { X, ChevronRight, Edit2, Trash2, Search, Package } from 'lucide-react'
import { toast } from 'react-hot-toast'
import ServiceTooltip from './ServiceTooltip'
import InfoTip from './InfoTip'
import EnhancedQuoteSectionEditor from './EnhancedQuoteSectionEditor'
import QuoteTermsManager from './QuoteTermsManager'
import { useCalculator } from './CalculatorContext'

const ServiceCalculatorCore = () => {
  const {
    // Navigation
    activeTab, setActiveTab,
    canAccessTab,
    // Services data
    services, packages, categoryMap, categoryOrder,
    servicesByCategory, filteredServicesByCategory,
    // Selection state
    selectedServices, setSelectedServices,
    selectedPackages,
    discount, setDiscount,
    // Search
    searchQuery, setSearchQuery,
    searchFilters, setSearchFilters,
    collapsedCategories,
    // Custom position
    isCustomPositionOpen, setIsCustomPositionOpen,
    customPosition, setCustomPosition,
    handleAddCustomPosition,
    handlePackageToggle,
    // Collapsibles
    isRabatteOpen, setIsRabatteOpen,
    // Quote sections
    quoteSections, setQuoteSections,
    savedQuoteId,
    // Quote Terms
    paymentTerms, setPaymentTerms,
    customPaymentTerms, setCustomPaymentTerms,
    savedCustomTerms,
    deliveryTime, setDeliveryTime,
    validity, setValidity,
    warranty, setWarranty,
    additionalInfo, setAdditionalInfo,
    quoteIntro, setQuoteIntro,
    heroHeadline, setHeroHeadline,
    heroSubline, setHeroSubline,
    nextSteps, setNextSteps,
    isIntroDialogOpen, setIsIntroDialogOpen,
    isGeneratingIntro,
    introFormData, setIntroFormData,
    tempGeneratedIntro, setTempGeneratedIntro,
    tempAdditionalInfo, setTempAdditionalInfo,
    generateAIIntro, handleUseIntro,
    isZahlungsbedingungenOpen, setIsZahlungsbedingungenOpen,
    // Helpers
    toggleCategory, getCategoryIcon, getCategoryName,
  } = useCalculator()

  if (activeTab !== 'kalkulator') return null

  return (
              <div className="space-y-6">
                {/* Weiter Button OBEN */}
                {canAccessTab('pdf') && Object.keys(selectedServices).length > 0 && (
                  <div className="p-4 bg-slate-50 rounded-lg border border-slate-200">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center text-slate-700">
                        <CheckCircleIcon className="h-5 w-5 mr-2 text-emerald-500" />
                        <span className="font-medium">Services ausgewählt: {Object.keys(selectedServices).length} Position{Object.keys(selectedServices).length !== 1 ? 'en' : ''}</span>
                      </div>
                      <Button
                        onClick={() => setActiveTab('pdf')}
                        className="bg-slate-900 hover:bg-slate-800 text-white"
                      >
                        Weiter
                        <ChevronRightIcon className="h-4 w-4 ml-2" />
                      </Button>
                    </div>
                  </div>
                )}
                
                {/* Service-Pakete - ganz oben */}
                {packages.length > 0 && (
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-slate-800">📦 Service-Pakete</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p className="text-sm text-slate-600 mb-4">
                        Wählen Sie vorgefertigte Service-Pakete für dieses Angebot:
                      </p>
                      <div className="space-y-3">
                        {packages.map((pkg) => {
                          const isSelected = selectedPackages[pkg.id]
                          const packagePrice = pkg.totalPrice || 0
                          
                          return (
                            <div
                              key={pkg.id}
                              className={`border rounded-lg p-4 cursor-pointer transition-all ${
                                isSelected
                                  ? 'border-slate-900 bg-slate-50'
                                  : 'border-slate-200 hover:border-blue-300'
                              }`}
                              onClick={() => handlePackageToggle(pkg.id)}
                            >
                              <div className="flex items-start justify-between">
                                <div className="flex items-start space-x-3 flex-1">
                                  <div onClick={(e) => e.stopPropagation()}>
                                    <Checkbox
                                      checked={!!isSelected}
                                      onCheckedChange={() => handlePackageToggle(pkg.id)}
                                      className="mt-1"
                                    />
                                  </div>
                                  <div className="flex-1">
                                    <h4 className="font-semibold text-slate-800">{pkg.name}</h4>
                                    {pkg.description && (
                                      <p className="text-sm text-slate-600 mt-1">{pkg.description}</p>
                                    )}
                                    <div className="mt-2 text-xs text-slate-500">
                                      {pkg.serviceIds?.length || 0} Services enthalten
                                    </div>
                                  </div>
                                </div>
                                <div className="text-right ml-4">
                                  <div className="font-bold text-lg text-slate-800">
                                    €{packagePrice.toFixed(2)}
                                  </div>
                                  {pkg.discountType === 'percentage' && pkg.discountValue > 0 && (
                                    <div className="text-xs text-green-600">
                                      {pkg.discountValue}% Rabatt
                                    </div>
                                  )}
                                </div>
                              </div>
                            </div>
                          )
                        })}
                      </div>
                    </CardContent>
                  </Card>
                )}
                
                {/* Einmalige Positionen anzeigen */}
                {Object.values(selectedServices).filter(s => s.isCustom).length > 0 && (
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-slate-800">📝 Individuelle Positionen</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-3">
                        {Object.values(selectedServices).filter(s => s.isCustom).map((position) => (
                          <div
                            key={position.id}
                            className="border rounded-lg p-4 bg-slate-50"
                          >
                            <div className="flex items-start justify-between">
                              <div className="flex-1">
                                <div className="flex items-center gap-2">
                                  <h4 className="font-semibold text-slate-800">{position.name}</h4>
                                  {position.isOptional && (
                                    <Badge className="bg-amber-100 text-amber-800 border-amber-300 text-xs">
                                      Optional
                                    </Badge>
                                  )}
                                </div>
                                {position.description && (
                                  <p className="text-sm text-slate-600 mt-1">{position.description}</p>
                                )}
                                <div className="flex items-center gap-4 mt-2 text-xs text-slate-500">
                                  <span>Menge: {position.quantity} {position.unit || 'Stk.'}</span>
                                  <span>
                                    Preis: €{position.price.toFixed(2)}
                                    {Number(position.priceMax) > position.price && ` – €${Number(position.priceMax).toFixed(2)} (Spanne)`}
                                  </span>
                                </div>
                              </div>
                              <div className="flex items-center gap-2 ml-4">
                                <div className="font-bold text-lg text-slate-800">
                                  €{(position.quantity * position.price).toFixed(2)}
                                </div>
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  onClick={() => {
                                    // Edit position - populate form with existing values
                                    setCustomPosition({
                                      name: position.name,
                                      description: position.description || '',
                                      detailedDescription: position.detailedDescription || '',
                                      price: position.price.toString(),
                                      priceMax: position.priceMax ? String(position.priceMax) : '',
                                      quantity: position.quantity,
                                      unit: position.unit || 'Stk.',
                                      type: position.type,
                                      category: position.category,
                                      isOptional: !!position.isOptional,
                                      saveForFuture: false,
                                      editingId: position.id
                                    })
                                    setIsCustomPositionOpen(true)
                                  }}
                                >
                                  <Edit2 className="w-4 h-4" />
                                </Button>
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  onClick={() => {
                                    // Remove position from selectedServices
                                    setSelectedServices(prev => {
                                      const updated = { ...prev }
                                      delete updated[position.id]
                                      return updated
                                    })
                                    toast.success('Position entfernt')
                                  }}
                                >
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* Custom Position Button */}
                <Card>
                  <CardContent className="pt-6">
                    <Dialog open={isCustomPositionOpen} onOpenChange={setIsCustomPositionOpen}>
                      <DialogTrigger asChild>
                        <Button variant="outline" className="w-full border-dashed border-2 border-slate-300 hover:border-slate-500 hover:bg-slate-50">
                          <span className="text-lg mr-2">+</span>
                          Individuelle Position hinzufügen
                        </Button>
                      </DialogTrigger>
                      <DialogContent className="max-w-2xl">
                        <DialogHeader>
                          <DialogTitle>Neue Position hinzufügen</DialogTitle>
                        </DialogHeader>
                        
                        <div className="space-y-4">
                          <div className="grid grid-cols-2 gap-4">
                            <div className="col-span-2">
                              <Label htmlFor="custom-name">Position Name *</Label>
                              <Input
                                id="custom-name"
                                value={customPosition.name}
                                onChange={(e) => setCustomPosition(prev => ({ ...prev, name: e.target.value }))}
                                placeholder="z.B. Spezielle Programmierung"
                              />
                            </div>
                            
                            <div>
                              <Label htmlFor="custom-price">Preis (€) *</Label>
                              <Input
                                id="custom-price"
                                type="number"
                                step="0.01"
                                min="0"
                                value={customPosition.price}
                                onChange={(e) => setCustomPosition(prev => ({ ...prev, price: e.target.value }))}
                                placeholder="0.00"
                              />
                            </div>

                            <div>
                              <Label htmlFor="custom-price-max">Preis bis (€, optional — Spanne)</Label>
                              <Input
                                id="custom-price-max"
                                type="number"
                                step="0.01"
                                min="0"
                                value={customPosition.priceMax || ''}
                                onChange={(e) => setCustomPosition(prev => ({ ...prev, priceMax: e.target.value }))}
                                placeholder="leer = Festpreis"
                              />
                              <p className="text-xs text-slate-500 mt-1">
                                Zeigt dem Kunden „von – bis". Verbindlich (und angenommen) wird der untere Wert;
                                mehr nur nach Abstimmung und Freigabe.
                              </p>
                            </div>

                            <div>
                              <Label htmlFor="custom-quantity">Menge</Label>
                              <Input
                                id="custom-quantity"
                                type="number"
                                min="1"
                                value={customPosition.quantity}
                                onChange={(e) => setCustomPosition(prev => ({ ...prev, quantity: parseInt(e.target.value) || 1 }))}
                              />
                            </div>
                            
                            <div>
                              <Label htmlFor="custom-discount">Rabatt (optional)</Label>
                              <div className="flex gap-2">
                                <Input
                                  id="custom-discount"
                                  type="number"
                                  min="0"
                                  placeholder="0"
                                  value={customPosition.discountValue || ''}
                                  onChange={(e) => setCustomPosition(prev => ({ ...prev, discountValue: e.target.value }))}
                                  className="flex-1"
                                />
                                <Select 
                                  value={customPosition.discountType || 'percentage'}
                                  onValueChange={(value) => setCustomPosition(prev => ({ ...prev, discountType: value }))}
                                >
                                  <SelectTrigger className="w-24">
                                    <SelectValue />
                                  </SelectTrigger>
                                  <SelectContent>
                                    <SelectItem value="percentage">%</SelectItem>
                                    <SelectItem value="fixed">€</SelectItem>
                                  </SelectContent>
                                </Select>
                              </div>
                            </div>
                            
                            <div>
                              <Label htmlFor="custom-type">Abrechnungsart</Label>
                              <Select 
                                value={customPosition.type}
                                onValueChange={(value) => setCustomPosition(prev => ({ ...prev, type: value }))}
                              >
                                <SelectTrigger>
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="einmalig">Einmalig</SelectItem>
                                  <SelectItem value="monatlich">Monatlich</SelectItem>
                                  <SelectItem value="jährlich">Jährlich</SelectItem>
                                  <SelectItem value="variabel">Variabel (Pay-as-you-go)</SelectItem>
                                </SelectContent>
                              </Select>
                            </div>
                            
                            <div>
                              <Label htmlFor="custom-category">Kategorie</Label>
                              <Select 
                                value={customPosition.category}
                                onValueChange={(value) => setCustomPosition(prev => ({ ...prev, category: value }))}
                              >
                                <SelectTrigger>
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  {categoryOrder.map(catId => {
                                    const cat = categoryMap[catId]
                                    if (!cat) return null
                                    return (
                                      <SelectItem key={catId} value={catId}>
                                        {cat.icon} {cat.name}
                                      </SelectItem>
                                    )
                                  })}
                                  <SelectItem value="other">⚙️ Sonstige</SelectItem>
                                </SelectContent>
                              </Select>
                            </div>
                          </div>
                          
                          <div>
                            <Label htmlFor="custom-description">Kurzbeschreibung</Label>
                            <Textarea
                              id="custom-description"
                              value={customPosition.description}
                              onChange={(e) => setCustomPosition(prev => ({ ...prev, description: e.target.value }))}
                              placeholder="Kurze Beschreibung der Position..."
                              rows={2}
                            />
                          </div>
                          
                          <div>
                            <Label htmlFor="custom-detailed">Detaillierte Beschreibung (für PDF)</Label>
                            <Textarea
                              id="custom-detailed"
                              value={customPosition.detailedDescription}
                              onChange={(e) => setCustomPosition(prev => ({ ...prev, detailedDescription: e.target.value }))}
                              placeholder="Detaillierte Beschreibung für das PDF-Angebot..."
                              rows={4}
                            />
                          </div>
                          
                          <div className="flex items-center space-x-2 bg-amber-50 p-4 rounded-lg border border-amber-200">
                            <Checkbox
                              id="custom-optional"
                              checked={!!customPosition.isOptional}
                              onCheckedChange={(checked) => setCustomPosition(prev => ({ ...prev, isOptional: checked }))}
                            />
                            <Label htmlFor="custom-optional" className="cursor-pointer">
                              ☑️ Optionale Position
                              <p className="text-xs text-slate-600 mt-1">
                                Der Kunde kann diese Position auf der Angebots-Seite selbst an- oder abwählen
                              </p>
                            </Label>
                          </div>

                          <div className="flex items-center space-x-2 bg-slate-50 p-4 rounded-lg border border-slate-200">
                            <Checkbox
                              id="save-future"
                              checked={customPosition.saveForFuture}
                              onCheckedChange={(checked) => setCustomPosition(prev => ({ ...prev, saveForFuture: checked }))}
                            />
                            <Label htmlFor="save-future" className="cursor-pointer">
                              💾 Position für zukünftige Angebote speichern
                              <p className="text-xs text-slate-600 mt-1">
                                Diese Position wird dauerhaft gespeichert und kann in zukünftigen Angeboten wiederverwendet werden
                              </p>
                            </Label>
                          </div>
                          
                          <div className="flex gap-3 pt-4">
                            <Button onClick={handleAddCustomPosition} className="flex-1">
                              Position hinzufügen
                            </Button>
                            <Button variant="outline" onClick={() => setIsCustomPositionOpen(false)}>
                              Abbrechen
                            </Button>
                          </div>
                        </div>
                      </DialogContent>
                    </Dialog>
                  </CardContent>
                </Card>
                
                {/* Services Section */}
                <Card>
                  <CardHeader>
                    <CardTitle className="text-slate-800">🛠️ Services</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {/* Smart Search Field */}
                    <div className="space-y-3 pb-4 border-b border-slate-200">
                      {/* Search Input */}
                      <div className="relative">
                        <Search className="absolute left-3 top-3 w-5 h-5 text-slate-400" />
                        <Input
                          type="text"
                          placeholder="Services durchsuchen (Name, Beschreibung)..."
                          value={searchQuery}
                          onChange={(e) => setSearchQuery(e.target.value)}
                          className="pl-10 pr-10"
                        />
                        {searchQuery && (
                          <button
                            onClick={() => setSearchQuery('')}
                            className="absolute right-3 top-3 text-slate-400 hover:text-slate-600"
                          >
                            <X className="w-5 h-5" />
                          </button>
                        )}
                      </div>
                      
                      {/* Filter Chips */}
                      <div className="flex flex-wrap gap-2">
                        {/* Type Filter */}
                        <Select 
                          value={searchFilters.type}
                          onValueChange={(value) => setSearchFilters(prev => ({ ...prev, type: value }))}
                        >
                          <SelectTrigger className="w-auto">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="all">Alle Typen</SelectItem>
                            <SelectItem value="einmalig">Einmalig</SelectItem>
                            <SelectItem value="monatlich">Monatlich</SelectItem>
                            <SelectItem value="jährlich">Jährlich</SelectItem>
                            <SelectItem value="variabel">Variabel</SelectItem>
                          </SelectContent>
                        </Select>
                        
                        {/* Price Range Filter */}
                        <Select 
                          value={searchFilters.priceRange}
                          onValueChange={(value) => setSearchFilters(prev => ({ ...prev, priceRange: value }))}
                        >
                          <SelectTrigger className="w-auto">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="all">Alle Preise</SelectItem>
                            <SelectItem value="under-100">Unter €100</SelectItem>
                            <SelectItem value="100-500">€100 - €500</SelectItem>
                            <SelectItem value="500-1000">€500 - €1.000</SelectItem>
                            <SelectItem value="over-1000">Über €1.000</SelectItem>
                          </SelectContent>
                        </Select>
                        
                        {/* Category Filter */}
                        <Select 
                          value={searchFilters.category}
                          onValueChange={(value) => setSearchFilters(prev => ({ ...prev, category: value }))}
                        >
                          <SelectTrigger className="w-auto">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="all">Alle Kategorien</SelectItem>
                            {Object.keys(servicesByCategory).map(cat => (
                              <SelectItem key={cat} value={cat}>
                                {getCategoryIcon(cat)} {getCategoryName(cat)}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        
                        {/* Clear Filters Button */}
                        {(searchQuery || searchFilters.type !== 'all' || searchFilters.priceRange !== 'all' || searchFilters.category !== 'all') && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                              setSearchQuery('')
                              setSearchFilters({ type: 'all', priceRange: 'all', category: 'all' })
                            }}
                            className="text-slate-600"
                          >
                            <X className="w-4 h-4 mr-1" />
                            Filter zurücksetzen
                          </Button>
                        )}
                      </div>
                      
                      {/* Results Count */}
                      {(searchQuery || searchFilters.type !== 'all' || searchFilters.priceRange !== 'all' || searchFilters.category !== 'all') && (
                        <div className="text-sm text-slate-600">
                          {Object.values(filteredServicesByCategory).reduce((sum, services) => sum + services.length, 0)} Ergebnisse gefunden
                        </div>
                      )}
                    </div>
                    
                    {/* Service Categories */}
                    {Object.entries(filteredServicesByCategory)
                      .sort(([catA], [catB]) => {
                        // Sort by categoryOrder (from API)
                        const orderA = categoryOrder.indexOf(catA)
                        const orderB = categoryOrder.indexOf(catB)
                        // If not found in order array, put at end
                        if (orderA === -1 && orderB === -1) return 0
                        if (orderA === -1) return 1
                        if (orderB === -1) return -1
                        return orderA - orderB
                      })
                      .map(([category, categoryServices]) => {
                  const isCollapsed = collapsedCategories[category]
                  const selectedInCategory = categoryServices.filter(s => selectedServices[s.id]).length
                  
                  return (
                    <Card key={category}>
                      <CardHeader 
                        className="cursor-pointer hover:bg-slate-50 transition-colors"
                        onClick={() => toggleCategory(category)}
                      >
                        <CardTitle className="flex items-center justify-between text-slate-800">
                          <div className="flex items-center">
                            <span className="text-2xl mr-3">{getCategoryIcon(category)}</span>
                            {getCategoryName(category)}
                            <span className="ml-3 text-sm font-normal text-slate-500">
                              ({selectedInCategory}/{categoryServices.length})
                            </span>
                          </div>
                          <button 
                            className="p-1 hover:bg-slate-200 rounded transition-colors"
                            onClick={(e) => {
                              e.stopPropagation()
                              toggleCategory(category)
                            }}
                          >
                            {isCollapsed ? (
                              <ChevronRightIcon className="w-5 h-5" />
                            ) : (
                              <ChevronDownIcon className="w-5 h-5" />
                            )}
                          </button>
                        </CardTitle>
                      </CardHeader>
                      {!isCollapsed && (
                        <CardContent>
                      <div className="grid grid-cols-1 gap-4">
                        {categoryServices.map(service => {
                          const isSelected = !!selectedServices[service.id]
                          const serviceData = selectedServices[service.id]
                          
                          return (
                            <div key={service.id} className={`p-4 rounded-lg border transition-all ${
                              isSelected ? 'border-slate-900 bg-slate-50' : 'border-slate-200 bg-white'
                            }`}>
                              <div className="flex items-start space-x-3">
                                <Checkbox
                                  checked={isSelected}
                                  onCheckedChange={() => {
                                    if (isSelected) {
                                      const newServices = { ...selectedServices }
                                      delete newServices[service.id]
                                      setSelectedServices(newServices)
                                    } else {
                                      setSelectedServices({
                                        ...selectedServices,
                                        [service.id]: {
                                          ...service,
                                          originalPrice: service.price,
                                          quantity: service.defaultQuantity || 1, // Use pre-filled defaultQuantity
                                          isOptional: false,
                                          isIncluded: true,
                                          // New fields
                                          customDiscount: { type: 'percentage', value: 0 },
                                          notes: '', // For telephone conversation notes
                                          alternatives: [], // Alternative Positionen
                                          selectedAlternativeId: null // Gewählte Alternative
                                        }
                                      })
                                    }
                                  }}
                                  className="mt-1"
                                />
                                <div className="flex-1">
                                  <div className="flex items-center space-x-2">
                                    <h3 className="font-semibold text-slate-800">{service.name}</h3>
                                    {(service.detailedDescription || service.description) && (
                                      <ServiceTooltip service={service}>
                                        <span></span>
                                      </ServiceTooltip>
                                    )}
                                  </div>
                                  <p className="text-slate-600 text-sm">{service.description}</p>
                                  
                                  {/* Service Type Badge */}
                                  <div className="mt-2">
                                    <Badge 
                                      className={
                                        service.type === 'einmalig' ? 'bg-green-100 text-green-800 border-green-200' :
                                        service.type === 'monatlich' ? 'bg-orange-100 text-orange-800 border-orange-200' :
                                        service.type === 'variabel' ? 'bg-slate-100 text-slate-700 border-slate-200' :
                                        'bg-slate-100 text-slate-700 border-slate-200'
                                      }
                                    >
                                      {service.type === 'einmalig' ? 'Einmalig' : 
                                       service.type === 'monatlich' ? 'Monatlich' :
                                       service.type === 'variabel' ? 'Variabel' :
                                       'Jährlich'}
                                    </Badge>
                                  </div>
                                  
                                  {/* Service-Typ Auswahl: Optional ODER Alternativposition */}
                                  {isSelected && (
                                    <div className="mt-3 space-y-3">
                                      <div className="flex items-center space-x-4">
                                        {/* Optional Checkbox */}
                                        <div className="flex items-center space-x-2">
                                          <Checkbox
                                            checked={serviceData?.isOptional || false}
                                            onCheckedChange={(checked) => {
                                              setSelectedServices({
                                                ...selectedServices,
                                                [service.id]: {
                                                  ...selectedServices[service.id],
                                                  isOptional: checked,
                                                  // Wenn Optional aktiviert wird, Alternative deaktivieren
                                                  hasAlternative: checked ? false : serviceData?.hasAlternative
                                                }
                                              })
                                            }}
                                          />
                                          <span className="text-sm text-orange-600 font-medium">Optional</span>
                                          <InfoTip text="Optionale Position: Der Kunde kann sie auf der Angebotsseite selbst an- oder abwählen. Die Summe passt sich automatisch an. Gut für Zusatzleistungen, die nicht jeder braucht." />
                                        </div>

                                        {/* Alternativposition Checkbox */}
                                        <div className="flex items-center space-x-2">
                                          <Checkbox
                                            checked={serviceData?.hasAlternative || false}
                                            onCheckedChange={(checked) => {
                                              setSelectedServices({
                                                ...selectedServices,
                                                [service.id]: {
                                                  ...selectedServices[service.id],
                                                  hasAlternative: checked,
                                                  // Wenn Alternative aktiviert wird, Optional deaktivieren
                                                  isOptional: checked ? false : serviceData?.isOptional,
                                                  // Alternative-Daten initialisieren wenn aktiviert
                                                  alternativeName: checked ? (serviceData?.alternativeName || `${service.name} Premium`) : '',
                                                  alternativePrice: checked ? (serviceData?.alternativePrice || service.price * 1.5) : 0,
                                                  alternativeDescription: checked ? (serviceData?.alternativeDescription || '') : '',
                                                  alternativeSelected: false // Standard: Hauptposition aktiv
                                                }
                                              })
                                            }}
                                          />
                                          <span className="text-sm text-slate-500 font-medium">Alternativposition</span>
                                          <InfoTip text="Alternativposition: Du bietest eine zweite Variante derselben Leistung an (z.B. Standard vs. Premium). Der Kunde wählt auf der Angebotsseite genau eine — nur die gewählte fließt in die Summe." />
                                        </div>
                                      </div>
                                      
                                      {/* Alternative Position Details */}
                                      {serviceData?.hasAlternative && (
                                        <div className="pl-6 border-l-2 border-purple-300 space-y-2">
                                          <div className="text-xs text-slate-600 mb-2">
                                            Die Hauptleistung &quot;{service.name}&quot; wird ins Angebot eingefügt. 
                                            Definieren Sie hier die Alternative (wird als optionale Variante angezeigt):
                                          </div>
                                          
                                          <div>
                                            <label className="block text-xs font-medium text-slate-700 mb-1">
                                              Name der Alternative *
                                            </label>
                                            <Input
                                              type="text"
                                              value={serviceData?.alternativeName || ''}
                                              onChange={(e) => {
                                                setSelectedServices({
                                                  ...selectedServices,
                                                  [service.id]: {
                                                    ...selectedServices[service.id],
                                                    alternativeName: e.target.value
                                                  }
                                                })
                                              }}
                                              placeholder="z.B. Logo Premium"
                                              className="text-sm"
                                            />
                                          </div>
                                          
                                          <div>
                                            <label className="block text-xs font-medium text-slate-700 mb-1">
                                              Preis der Alternative * (€)
                                            </label>
                                            <Input
                                              type="number"
                                              step="0.01"
                                              value={serviceData?.alternativePrice || ''}
                                              onChange={(e) => {
                                                setSelectedServices({
                                                  ...selectedServices,
                                                  [service.id]: {
                                                    ...selectedServices[service.id],
                                                    alternativePrice: parseFloat(e.target.value) || 0
                                                  }
                                                })
                                              }}
                                              placeholder="Preis"
                                              className="text-sm"
                                            />
                                          </div>
                                          
                                          <div>
                                            <label className="block text-xs font-medium text-slate-700 mb-1">
                                              Beschreibung (optional)
                                            </label>
                                            <Textarea
                                              value={serviceData?.alternativeDescription || ''}
                                              onChange={(e) => {
                                                setSelectedServices({
                                                  ...selectedServices,
                                                  [service.id]: {
                                                    ...selectedServices[service.id],
                                                    alternativeDescription: e.target.value
                                                  }
                                                })
                                              }}
                                              placeholder="Was macht diese Alternative besonders?"
                                              className="text-sm"
                                              rows={2}
                                            />
                                          </div>
                                          
                                          {/* Info: Alternative wird als Option angezeigt */}
                                          <div className="mt-3 p-2 bg-slate-50 rounded border border-slate-200">
                                            <p className="text-xs text-slate-600">
                                              ℹ️ Die Alternative wird im Angebot als Option angezeigt (eingerückt). 
                                              Der Kunde kann sie auf der Landingpage per Checkbox auswählen.
                                            </p>
                                          </div>
                                        </div>
                                      )}
                                    </div>
                                  )}
                                  
                                  {/* Quantity and Price Controls for ALL services */}
                                  {isSelected && (
                                    <div className="mt-3 space-y-3">
                                      {/* Menge und Einzelpreis in einer Zeile */}
                                      <div className="grid grid-cols-3 gap-3 items-end">
                                        <div>
                                          <label className="block text-xs font-medium text-slate-700 mb-1">
                                            Menge{service.unit ? ` (${service.unit})` : ''}
                                          </label>
                                          <Input
                                            type="number"
                                            value={serviceData?.quantity || 1}
                                            onChange={(e) => {
                                              const quantity = parseInt(e.target.value) || 1
                                              setSelectedServices({
                                                ...selectedServices,
                                                [service.id]: {
                                                  ...selectedServices[service.id],
                                                  quantity: quantity
                                                }
                                              })
                                            }}
                                            className="w-full"
                                            min="1"
                                            max="100"
                                            placeholder={service.unit || 'Menge'}
                                          />
                                        </div>
                                        
                                        <div>
                                          <label className="block text-xs font-medium text-slate-700 mb-1">Einzelpreis (€)</label>
                                          <Input
                                            type="number"
                                            value={serviceData?.price || service.price}
                                            onChange={(e) => {
                                              const price = parseFloat(e.target.value) || service.price
                                              setSelectedServices({
                                                ...selectedServices,
                                                [service.id]: {
                                                  ...selectedServices[service.id],
                                                  price: price
                                                }
                                              })
                                            }}
                                            className="w-full"
                                            min="0"
                                            step="0.01"
                                          />
                                        </div>
                                        
                                        <div>
                                          <label className="block text-xs font-medium text-slate-700 mb-1">Gesamt</label>
                                          <div className="px-3 py-2 bg-slate-100 rounded border text-sm font-medium">
                                            {(() => {
                                              const qty = serviceData?.quantity || 1;
                                              const unitPrice = serviceData?.price || service.price;
                                              let total;
                                              if (service.priceTiers && service.priceTiers.length > 0) {
                                                const tier = service.priceTiers.find(t => t.upTo === null || qty <= t.upTo)
                                                  || service.priceTiers[service.priceTiers.length - 1];
                                                total = (tier.pricePerUnit ?? unitPrice) * qty;
                                              } else {
                                                total = unitPrice * qty;
                                              }
                                              return `€${total.toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
                                            })()}
                                          </div>
                                          {service.priceTiers && service.priceTiers.length > 0 && (
                                            <p className="text-xs text-slate-500 mt-1">Staffelpreis aktiv</p>
                                          )}
                                        </div>
                                      </div>
                                    </div>
                                  )}
                                  
                                  {/* Notice */}
                                  {service.notice && (
                                    <div className="mt-2 p-2 bg-yellow-50 rounded text-xs text-yellow-800 flex items-start">
                                      <span className="mr-1">⚠</span>
                                      {service.notice}
                                    </div>
                                  )}

                                  {/* Individual Service Discount */}
                                  {isSelected && (
                                    <div className="mt-3 p-3 bg-slate-50 rounded-lg">
                                      <h4 className="text-xs font-medium text-slate-700 mb-2 flex items-center gap-1">Service-Rabatt<InfoTip text="Rabatt nur auf diese eine Position — als Prozent oder fester Betrag. Wirkt zusätzlich zu einem evtl. Gesamtrabatt und erscheint korrekt im PDF." /></h4>
                                      <div className="grid grid-cols-3 gap-3 items-end">
                                        <div>
                                          <label className="block text-xs text-slate-600 mb-1">Art</label>
                                          <Select
                                            value={serviceData?.customDiscount?.type || 'percentage'}
                                            onValueChange={(value) => {
                                              setSelectedServices({
                                                ...selectedServices,
                                                [service.id]: {
                                                  ...selectedServices[service.id],
                                                  customDiscount: { 
                                                    type: value,
                                                    value: serviceData?.customDiscount?.value || 0
                                                  }
                                                }
                                              })
                                            }}
                                          >
                                            <SelectTrigger className="text-xs h-8">
                                              <SelectValue />
                                            </SelectTrigger>
                                            <SelectContent>
                                              <SelectItem value="percentage">%</SelectItem>
                                              <SelectItem value="fixed">€</SelectItem>
                                            </SelectContent>
                                          </Select>
                                        </div>
                                        <div>
                                          <label className="block text-xs text-slate-600 mb-1">Wert</label>
                                          <Input
                                            type="number"
                                            value={serviceData?.customDiscount?.value || 0}
                                            onChange={(e) => {
                                              const value = parseFloat(e.target.value) || 0
                                              setSelectedServices({
                                                ...selectedServices,
                                                [service.id]: {
                                                  ...selectedServices[service.id],
                                                  customDiscount: { 
                                                    type: serviceData?.customDiscount?.type || 'percentage',
                                                    value: value 
                                                  }
                                                }
                                              })
                                            }}
                                            className="text-xs h-8"
                                            min="0"
                                            step="0.01"
                                          />
                                        </div>
                                        <div className="text-right">
                                          {serviceData?.customDiscount?.value > 0 && (
                                            <span className="text-xs text-green-600 font-medium">
                                              -{serviceData.customDiscount.value}{serviceData.customDiscount.type === 'percentage' ? '%' : '€'}
                                            </span>
                                          )}
                                        </div>
                                      </div>
                                    </div>
                                  )}

                                  {/* Individual Notes Field */}
                                  {isSelected && (
                                    <div className="mt-3">
                                      <label className="block text-xs font-medium text-slate-700 mb-1">Telefonat-Notizen</label>
                                      <Textarea
                                        value={serviceData?.notes || ''}
                                        onChange={(e) => {
                                          setSelectedServices({
                                            ...selectedServices,
                                            [service.id]: {
                                              ...selectedServices[service.id],
                                              notes: e.target.value
                                            }
                                          })
                                        }}
                                        placeholder="Kommentare während Telefonat..."
                                        className="text-sm"
                                        rows={2}
                                      />
                                    </div>
                                  )}
                                </div>
                                
                                <div className="text-right">
                                  <div className="font-bold text-xl text-slate-800">
                                    {isSelected && serviceData ? 
                                      (() => {
                                        const qty = serviceData.quantity || 1;
                                        const unitPrice = serviceData.price || service.price;
                                        let total;
                                        if (service.priceTiers && service.priceTiers.length > 0) {
                                          const tier = service.priceTiers.find(t => t.upTo === null || qty <= t.upTo)
                                            || service.priceTiers[service.priceTiers.length - 1];
                                          total = (tier.pricePerUnit ?? unitPrice) * qty;
                                        } else {
                                          total = unitPrice * qty;
                                        }
                                        return `${total.toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}€`;
                                      })() :
                                      `${service.price.toLocaleString('de-DE', {
                                        minimumFractionDigits: 2,
                                        maximumFractionDigits: 2
                                      })}€`
                                    }
                                  </div>
                                  {isSelected && serviceData?.isOptional && (
                                    <div className="text-xs text-orange-600 mt-1">
                                      Nicht im Endpreis enthalten
                                    </div>
                                  )}
                                </div>
                              </div>
                            </div>
                          )
                        })}
                      </div>
                    </CardContent>
                      )}
                    </Card>
                  )
                })}
                  </CardContent>
                </Card>

                {/* Rabatte & Nachlässe Section */}
                <Card>
                  <Collapsible open={isRabatteOpen} onOpenChange={setIsRabatteOpen}>
                    <CardHeader className="cursor-pointer" onClick={() => setIsRabatteOpen(!isRabatteOpen)}>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <CardTitle className="text-slate-800 flex items-center gap-2">Rabatte & Nachlässe<InfoTip text="Gesamtrabatt getrennt für einmalige, monatliche und jährliche Kosten — als Prozent oder fester Betrag. Die Begründung erscheint im Angebot und schafft Transparenz beim Kunden." /></CardTitle>
                          {(discount.oneTime?.value > 0 || discount.monthly?.value > 0 || discount.yearly?.value > 0) && (
                            <Badge variant="secondary">
                              {discount.oneTime?.value > 0 && `Einmalig: ${discount.oneTime.value}${discount.oneTime.type === 'percentage' ? '%' : '€'}`}
                              {discount.oneTime?.value > 0 && (discount.monthly?.value > 0 || discount.yearly?.value > 0) && ' | '}
                              {discount.monthly?.value > 0 && `Monatlich: ${discount.monthly.value}${discount.monthly.type === 'percentage' ? '%' : '€'}`}
                              {discount.monthly?.value > 0 && discount.yearly?.value > 0 && ' | '}
                              {discount.yearly?.value > 0 && `Jährlich: ${discount.yearly.value}${discount.yearly.type === 'percentage' ? '%' : '€'}`}
                            </Badge>
                          )}
                        </div>
                        <ChevronRight className={`w-5 h-5 transition-transform ${isRabatteOpen ? 'rotate-90' : ''}`} />
                      </div>
                    </CardHeader>
                    <CollapsibleContent>
                      <CardContent>
                    <div className="space-y-6">
                      {/* One-time discount */}
                      <div>
                        <h3 className="font-semibold text-green-600 text-lg mb-3">Rabatt auf einmalige Kosten:</h3>
                        <div className="grid grid-cols-3 gap-4 items-end">
                          <div>
                            <label className="block text-sm font-medium text-slate-700 mb-2">Rabatt-Art</label>
                            <select 
                              value={discount.oneTime.type}
                              onChange={(e) => setDiscount({
                                ...discount,
                                oneTime: { ...discount.oneTime, type: e.target.value }
                              })}
                              className="w-full px-3 py-2 border border-slate-300 rounded-md"
                            >
                              <option value="percentage">Prozent (%)</option>
                              <option value="fixed">Betrag (€)</option>
                            </select>
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-slate-700 mb-2">Wert</label>
                            <div className="flex items-center">
                              <Input
                                type="number"
                                value={discount.oneTime.value}
                                onChange={(e) => setDiscount({
                                  ...discount,
                                  oneTime: { ...discount.oneTime, value: parseFloat(e.target.value) || 0 }
                                })}
                                className="w-full"
                                min="0"
                                step="0.01"
                              />
                              <span className="ml-2 text-slate-600">
                                {discount.oneTime.type === 'percentage' ? '%' : '€'}
                              </span>
                            </div>
                          </div>
                          <div className="text-right">
                            <span className="text-lg font-semibold text-slate-800">
                              {discount.oneTime.type === 'percentage' ? 
                                `${discount.oneTime.value}%` : 
                                `${discount.oneTime.value}€`
                              } Rabatt
                            </span>
                          </div>
                        </div>
                        
                        <div className="mt-3">
                          <label className="block text-sm font-medium text-slate-700 mb-2">Rabatt-Begründung (optional)</label>
                          <textarea
                            value={discount.oneTime.reason || ''}
                            onChange={(e) => setDiscount({
                              ...discount,
                              oneTime: { ...discount.oneTime, reason: e.target.value }
                            })}
                            placeholder="z.B. Wir können Ihnen diesen Rabatt geben, wenn wir die Designumsetzung von einem externen Partner umsetzen lassen..."
                            className="w-full px-3 py-2 border border-slate-300 rounded-md"
                            rows={3}
                          />
                        </div>
                      </div>

                      {/* Monthly discount */}
                      <div>
                        <h3 className="font-semibold text-orange-600 text-lg mb-3">Rabatt auf monatliche Kosten:</h3>
                        <div className="grid grid-cols-3 gap-4 items-end">
                          <div>
                            <label className="block text-sm font-medium text-slate-700 mb-2">Rabatt-Art</label>
                            <select 
                              value={discount.monthly?.type || 'percentage'}
                              onChange={(e) => setDiscount({
                                ...discount,
                                monthly: { ...discount.monthly, type: e.target.value }
                              })}
                              className="w-full px-3 py-2 border border-slate-300 rounded-md"
                            >
                              <option value="percentage">Prozent (%)</option>
                              <option value="fixed">Betrag (€)</option>
                            </select>
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-slate-700 mb-2">Wert</label>
                            <div className="flex items-center">
                              <Input
                                type="number"
                                value={discount.monthly?.value || 0}
                                onChange={(e) => setDiscount({
                                  ...discount,
                                  monthly: { ...discount.monthly, value: parseFloat(e.target.value) || 0 }
                                })}
                                className="w-full"
                                min="0"
                                step="0.01"
                              />
                              <span className="ml-2 text-slate-600">
                                {discount.monthly?.type === 'percentage' ? '%' : '€'}
                              </span>
                            </div>
                          </div>
                          <div className="text-right">
                            <span className="text-lg font-semibold text-slate-800">
                              {discount.monthly?.type === 'percentage' ? 
                                `${discount.monthly?.value || 0}%` : 
                                `${discount.monthly?.value || 0}€`
                              } Rabatt
                            </span>
                          </div>
                        </div>
                        
                        <div className="mt-3">
                          <label className="block text-sm font-medium text-slate-700 mb-2">Rabatt-Begründung (optional)</label>
                          <textarea
                            value={discount.monthly?.reason || ''}
                            onChange={(e) => setDiscount({
                              ...discount,
                              monthly: { ...discount.monthly, reason: e.target.value }
                            })}
                            placeholder="z.B. Rabatt bei längerfristiger Wartungsvereinbarung..."
                            className="w-full px-3 py-2 border border-slate-300 rounded-md"
                            rows={2}
                          />
                        </div>
                      </div>

                      {/* Yearly discount - NEU */}
                      <div>
                        <h3 className="font-semibold text-slate-800 text-lg mb-3">Rabatt auf jährliche Kosten:</h3>
                        <div className="grid grid-cols-3 gap-4 items-end">
                          <div>
                            <label className="block text-sm font-medium text-slate-700 mb-2">Rabatt-Art</label>
                            <select 
                              value={discount.yearly?.type || 'percentage'}
                              onChange={(e) => setDiscount({
                                ...discount,
                                yearly: { ...discount.yearly, type: e.target.value }
                              })}
                              className="w-full px-3 py-2 border border-slate-300 rounded-md"
                            >
                              <option value="percentage">Prozent (%)</option>
                              <option value="fixed">Betrag (€)</option>
                            </select>
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-slate-700 mb-2">Wert</label>
                            <div className="flex items-center">
                              <Input
                                type="number"
                                value={discount.yearly?.value || 0}
                                onChange={(e) => setDiscount({
                                  ...discount,
                                  yearly: { ...discount.yearly, value: parseFloat(e.target.value) || 0 }
                                })}
                                className="w-full"
                                min="0"
                                step="0.01"
                              />
                              <span className="ml-2 text-slate-600">
                                {discount.yearly?.type === 'percentage' ? '%' : '€'}
                              </span>
                            </div>
                          </div>
                          <div className="text-right">
                            <span className="text-lg font-semibold text-slate-800">
                              {discount.yearly?.type === 'percentage' ? 
                                `${discount.yearly?.value || 0}%` : 
                                `${discount.yearly?.value || 0}€`
                              } Rabatt
                            </span>
                          </div>
                        </div>
                        
                        <div className="mt-3">
                          <label className="block text-sm font-medium text-slate-700 mb-2">Rabatt-Begründung (optional)</label>
                          <textarea
                            value={discount.yearly?.reason || ''}
                            onChange={(e) => setDiscount({
                              ...discount,
                              yearly: { ...discount.yearly, reason: e.target.value }
                            })}
                            placeholder="z.B. Rabatt bei jährlicher Vorauszahlung..."
                            className="w-full px-3 py-2 border border-slate-300 rounded-md"
                            rows={2}
                          />
                        </div>
                      </div>

                      <div className="mt-4 p-3 bg-slate-50 rounded-lg">
                        <p className="text-sm text-slate-700">
                          <span className="mr-1">💡</span>
                          <strong>Tipp:</strong> Rabatte werden automatisch in der Preisberechnung und im PDF-Angebot berücksichtigt.
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </CollapsibleContent>
              </Collapsible>
            </Card>

                {/* Quote Terms: Zahlungsbedingungen, Lieferzeit, KI-Intro */}
                <QuoteTermsManager
                  paymentTerms={paymentTerms} setPaymentTerms={setPaymentTerms}
                  customPaymentTerms={customPaymentTerms} setCustomPaymentTerms={setCustomPaymentTerms}
                  savedCustomTerms={savedCustomTerms}
                  deliveryTime={deliveryTime} setDeliveryTime={setDeliveryTime}
                  validity={validity} setValidity={setValidity}
                  warranty={warranty} setWarranty={setWarranty}
                  additionalInfo={additionalInfo} setAdditionalInfo={setAdditionalInfo}
                  quoteIntro={quoteIntro} setQuoteIntro={setQuoteIntro}
                  heroHeadline={heroHeadline} setHeroHeadline={setHeroHeadline}
                  heroSubline={heroSubline} setHeroSubline={setHeroSubline}
                  nextSteps={nextSteps} setNextSteps={setNextSteps}
                  isIntroDialogOpen={isIntroDialogOpen} setIsIntroDialogOpen={setIsIntroDialogOpen}
                  isGeneratingIntro={isGeneratingIntro}
                  introFormData={introFormData} setIntroFormData={setIntroFormData}
                  tempGeneratedIntro={tempGeneratedIntro} setTempGeneratedIntro={setTempGeneratedIntro}
                  tempAdditionalInfo={tempAdditionalInfo} setTempAdditionalInfo={setTempAdditionalInfo}
                  generateAIIntro={generateAIIntro}
                  handleUseIntro={handleUseIntro}
                  isZahlungsbedingungenOpen={isZahlungsbedingungenOpen} setIsZahlungsbedingungenOpen={setIsZahlungsbedingungenOpen}
                />

                {/* Enhanced Quote Section Editor */}
                <EnhancedQuoteSectionEditor 
                  sections={quoteSections}
                  onSectionsChange={setQuoteSections}
                  quoteId={savedQuoteId}
                />
                
                {Object.keys(selectedServices).length > 0 && (
                  <div className="mt-6 space-y-4">
                    <div className="p-4 bg-slate-50 rounded-lg border border-slate-200">
                      <div className="flex items-center text-slate-700">
                        <CheckCircleIcon className="h-5 w-5 mr-2 text-emerald-500" />
                        <span className="font-medium">Services ausgewählt: {Object.keys(selectedServices).length} Position{Object.keys(selectedServices).length !== 1 ? 'en' : ''}</span>
                      </div>
                    </div>
                    <Button 
                      onClick={() => setActiveTab('pdf')}
                      className="w-full bg-slate-900 hover:bg-slate-800 text-white"
                    >
                      Weiter
                      <ChevronRightIcon className="h-4 w-4 ml-2" />
                    </Button>
                  </div>
                )}
              </div>
  )
}

export default ServiceCalculatorCore
